import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@components/Layout/Layout';
import Button from '@components/UI/Button';
import { showToast } from '@components/Layout/Toast';
import { showSpinner, hideSpinner } from '@components/Layout/LoadingSpinner';
import type { EntityType, TradingProfile, TradeFilter, BacktestSortingRule, PriceAdjustmentRule } from '@einhaus/project314-types';
import { trpc } from '@/api/trpc.ts';
import { useQueryClient } from '@tanstack/react-query';
import { handleTrpcQuery } from '@/api/trpcResponseHandler';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useScreenerRuleOptions } from '@/hooks/useScreenerRuleOptions';
import { useTradingRequisites } from '@/hooks/useTradingRequisites';
import ShareResourceModal from '@/components/resourceAccess/ShareResourceModal';
import BasicSettingsCard from './components/BasicSettingsCard';
import BrokerageOrderRulesCard from './components/BrokerageOrderRulesCard';
import EntryCollectionWindowCard from './components/EntryCollectionWindowCard';
import EntrySortingRulesSection from './components/EntrySortingRulesSection';
import TradeFilterModal from './components/TradeFilterModal';
import TradeFiltersSection from './components/TradeFiltersSection';
import UniverseScreenerSection from './components/UniverseScreenerSection';
import AeroExitSelectionSection from './components/AeroExitSelectionSection';
import BacktestSortingRuleModal from './components/BacktestSortingRuleModal';
import BacktestSortingRulesSection from './components/BacktestSortingRulesSection';
import LimitPriceAdjustmentRuleModal from './components/LimitPriceAdjustmentRuleModal';
import LimitPriceAdjustmentRulesSection from './components/LimitPriceAdjustmentRulesSection';
import CopyProfileModal from './components/CopyProfileModal';
import { isTradingProfilePathRecord, type TradingProfilePathRecord } from './components/helpers';
import type { NewBacktestSortingRuleState, NewLimitPriceAdjustmentRuleState, NewTradeFilterState } from './components/types';

const TradingProfileEditor: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const tradingProfileId = id ? parseInt(id) : 0;

    // Component state
    const [error, setError] = useState<string | null>(null);

    // Copy modal state
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [copyName, setCopyName] = useState('');
    const [isCopying, setIsCopying] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);

    const tradingProfile: TradingProfile = {
        id: 0,
        name: '',
        tradeFilters: [],
        allowedUnderlyingBias: 'both',
        allowedUnderlyingEntityTypes: [],
        backtestMetricScope: 'entity',
        backtestSelectionScope: 'entity',
        backtestSelectionPolicyKey: 'profile_metric_v1',
        backtestSelectionPolicyVersion: 1,
        backtestSelectionExecutionMode: 'authoritative',
        backtestSortingRules: [],
        entrySortingRules: [],
        deactivateOnFomcDays: false,
        backtestOptionTradeSizeDollars: null,
        maximumEntryPriceIncreasePercent: null,
        autoLiquidateAssignedShares: false,
        assignedShareLiquidationMinutesAfterOpen: null,
        minOptionPrice: null,
        brokerageOrderRules: {
            cancelUnfilledBuyOrdersAtClose: false,
            cancelUnfilledSellOrdersAtClose: false,
            priceAdjustmentRules: [],
            cancelUnfilledBuyOrderMinutes: null,
            cancelUnfilledSellOrderMinutes: null,
            buyLimitTargetPercentage: null,
            buyLimitBidAskAboveBelow: null,
            buyLimitBidAskTarget: null,
            sellLimitTargetPercentage: null,
            sellLimitBidAskAboveBelow: null,
            sellLimitBidAskTarget: null
        },
        entryCollectionWindowConfig: null
    };

    const [formData, setFormData] = useState<TradingProfile>(tradingProfile);
    // Lists derived from queries (see memos below)
    // Removed local copy state to avoid copy-effects

    // Additional state for selected items
    const [selectedEntityTypes, setSelectedEntityTypes] = useState<EntityType[]>([]);
    const [tradeFilters, setTradeFilters] = useState<TradeFilter[]>([]);
    const [backtestSortingRules, setBacktestSortingRules] = useState<BacktestSortingRule[]>([]);
    const [universeScreenerRuleId, setUniverseScreenerRuleId] = useState<number | null>(null);
    const [isUniverseScreenerActive, setIsUniverseScreenerActive] = useState<boolean>(true);

    // Modal state
    const [showTradeFilterModal, setShowTradeFilterModal] = useState(false);
    const [showBacktestSortingRulesModal, setShowBacktestSortingRulesModal] = useState(false);
    const [showLimitPriceAdjustmentRulesModal, setShowLimitPriceAdjustmentRulesModal] = useState(false);

    // Editing state (for "Add" modals)
    const [editingTradeFilterId, setEditingTradeFilterId] = useState<string | null>(null);
    const [editingBacktestSortingRuleId, setEditingBacktestSortingRuleId] = useState<string | null>(null);
    const [editingLimitPriceAdjustmentRuleId, setEditingLimitPriceAdjustmentRuleId] = useState<string | null>(null);

    // Modal form state
    const [newTradeFilter, setNewTradeFilter] = useState<NewTradeFilterState>({
        entityType: 'Asset',
        tradeDirection: 'either',
        property: '',
        comparison: 'greaterThan',
        value: null
    });

    const [newBacktestSortingRule, setNewBacktestSortingRule] = useState<NewBacktestSortingRuleState>({
        field: '',
        order: 'desc',
        tradeDirection: 'either'
    });

    const [newLimitPriceAdjustmentRule, setNewLimitPriceAdjustmentRule] = useState<NewLimitPriceAdjustmentRuleState>({
        tradeType: '',
        direction: '',
        percentage: null,
        minutes: null,
        priceReference: 'currentLimit',
        adjustmentUnit: 'percentage',
        adjustmentValue: null
    });

    // State for the lists of rules
    const [priceAdjustmentRules, setPriceAdjustmentRules] = useState<PriceAdjustmentRule[]>([]);

    // Fetch trading requisites with tRPC
    const { query: tradingRequisitesQuery } = useTradingRequisites({
        errorConfig: {
            fallbackErrorMessage: 'Failed to load trading requisites',
            setError: (msg) => setError(msg)
        }
    });

    // Fetch trading profile data if editing existing profile
    const tradingProfileQuery = useAppQuery({
        ...trpc.user.fetchTradingProfile.queryOptions({ id: tradingProfileId }),
        enabled: tradingProfileId > 0
    });

    const accessSummaryQuery = useAppQuery({
        ...trpc.resourceAccess.getAccessSummary.queryOptions({
            resourcePk: tradingProfileId,
            resourceType: 'tradingProfile'
        }),
        enabled: tradingProfileId > 0
    });

    const accessSummary = accessSummaryQuery.data;
    const canEditTradingProfile = tradingProfileId === 0 || accessSummary?.canEdit === true;
    const canCopyTradingProfile = tradingProfileId > 0 && accessSummary?.canCopy === true;
    const canManageSharing = accessSummary?.ownedByUser === true;

    const screenerRuleOptionsResult = useScreenerRuleOptions(universeScreenerRuleId);

    // Initialize form state when trading profile loads (guarded against repeats)
    const initializedRef = React.useRef<number | null>(null);

    useEffect(() => {
        const tradingProfileData = tradingProfileQuery.data?.settings;
        if (!tradingProfileData) return;
        if (initializedRef.current === tradingProfileId) return;
        initializedRef.current = tradingProfileId;

        const updatedFormData: TradingProfile = {
            ...tradingProfileData,
            backtestMetricScope: tradingProfileData.backtestMetricScope || 'entity',
            backtestSelectionScope: tradingProfileData.backtestSelectionScope ?? 'entity',
            backtestSelectionPolicyKey: tradingProfileData.backtestSelectionPolicyKey ?? 'profile_metric_v1',
            backtestSelectionPolicyVersion: tradingProfileData.backtestSelectionPolicyVersion ?? 1,
            backtestSelectionExecutionMode: tradingProfileData.backtestSelectionExecutionMode ?? 'authoritative',
            brokerageOrderRules: {
                ...tradingProfileData.brokerageOrderRules,
                cancelUnfilledBuyOrdersAtClose: tradingProfileData.brokerageOrderRules?.cancelUnfilledBuyOrdersAtClose || false,
                cancelUnfilledSellOrdersAtClose: tradingProfileData.brokerageOrderRules?.cancelUnfilledSellOrdersAtClose || false,
                priceAdjustmentRules: tradingProfileData.brokerageOrderRules?.priceAdjustmentRules || [],
                cancelUnfilledBuyOrderMinutes: tradingProfileData.brokerageOrderRules?.cancelUnfilledBuyOrderMinutes || null,
                cancelUnfilledSellOrderMinutes: tradingProfileData.brokerageOrderRules?.cancelUnfilledSellOrderMinutes || null,
                buyLimitTargetPercentage: tradingProfileData.brokerageOrderRules?.buyLimitTargetPercentage || null,
                buyLimitBidAskAboveBelow: tradingProfileData.brokerageOrderRules?.buyLimitBidAskAboveBelow || null,
                buyLimitBidAskTarget: tradingProfileData.brokerageOrderRules?.buyLimitBidAskTarget || null,
                sellLimitTargetPercentage: tradingProfileData.brokerageOrderRules?.sellLimitTargetPercentage || null,
                sellLimitBidAskAboveBelow: tradingProfileData.brokerageOrderRules?.sellLimitBidAskAboveBelow || null,
                sellLimitBidAskTarget: tradingProfileData.brokerageOrderRules?.sellLimitBidAskTarget || null
            }
        };

        setFormData(updatedFormData);

        if (tradingProfileData.allowedUnderlyingEntityTypes) {
            setSelectedEntityTypes(tradingProfileData.allowedUnderlyingEntityTypes);
        }

        if (tradingProfileData.tradeFilters) {
            setTradeFilters(tradingProfileData.tradeFilters);
        }

        if (tradingProfileData.backtestSortingRules) {
            setBacktestSortingRules(tradingProfileData.backtestSortingRules);
        }

        if (tradingProfileData.brokerageOrderRules?.priceAdjustmentRules) {
            setPriceAdjustmentRules(tradingProfileData.brokerageOrderRules.priceAdjustmentRules);
        }

        const universeScreenerLink = tradingProfileQuery.data?.universeScreenerLink ?? null;
        setUniverseScreenerRuleId(universeScreenerLink?.screenerRuleId ?? null);
        setIsUniverseScreenerActive(universeScreenerLink?.isActive === 1);
    }, [tradingProfileQuery.data, tradingProfileId]);

    // Handle query errors
    useEffect(() => {
        if (tradingProfileQuery.error && tradingProfileId > 0) {
            handleTrpcQuery(tradingProfileQuery, {
                fallbackErrorMessage: 'Failed to load trading profile data',
                setError: (msg) => setError(msg)
            });
        }
    }, [tradingProfileQuery.error, tradingProfileId]);

    useEffect(() => {
        if (accessSummaryQuery.error && tradingProfileId > 0) {
            handleTrpcQuery(accessSummaryQuery, {
                fallbackErrorMessage: 'Failed to load trading profile access',
                setError: (msg) => setError(msg)
            });
        }
    }, [accessSummaryQuery.error, tradingProfileId]);

    useEffect(() => {
        if (screenerRuleOptionsResult.listQuery.error) {
            handleTrpcQuery(screenerRuleOptionsResult.listQuery, {
                fallbackErrorMessage: 'Failed to load screener rules',
                setError: (msg) => setError(msg)
            });
        }
    }, [screenerRuleOptionsResult.listQuery.error]);

    // Calculate loading state from React Query's loading states
    const loading = useMemo(() => {
        return (
            tradingRequisitesQuery.isPending ||
            screenerRuleOptionsResult.listQuery.isPending ||
            (tradingProfileId > 0 && (tradingProfileQuery.isPending || accessSummaryQuery.isPending))
        );
    }, [
        tradingRequisitesQuery.isPending,
        screenerRuleOptionsResult.listQuery.isPending,
        tradingProfileQuery.isPending,
        accessSummaryQuery.isPending,
        tradingProfileId
    ]);

    // Show/hide spinner based on loading state
    useEffect(() => {
        if (loading) {
            showSpinner();
        } else {
            hideSpinner();
        }

        // Hide spinner when component unmounts
        return () => {
            hideSpinner();
        };
    }, [loading]);

    // Helper function to set nested property efficiently
    const setNestedProperty = useCallback((obj: TradingProfile, path: string, value: string | boolean): TradingProfile => {
        const pathParts = path.split('.');

        // For top-level properties, just spread and update
        if (pathParts.length === 1) {
            return {
                ...obj,
                [pathParts[0]]: value
            };
        }

        // For nested properties, only clone the affected path
        const result = { ...obj };
        let current = result as TradingProfilePathRecord;

        // Navigate and shallow clone only the necessary objects
        for (let i = 0; i < pathParts.length - 1; i++) {
            const part = pathParts[i];
            const currentValue = current[part];
            const nextValue: TradingProfilePathRecord = isTradingProfilePathRecord(currentValue) ? { ...currentValue } : {};
            current[part] = nextValue;
            current = nextValue;
        }

        // Set the final value
        current[pathParts[pathParts.length - 1]] = value;

        return result;
    }, []);

    // Handle input changes
    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
            const { id, value, type } = e.target;
            const dataName = e.target.getAttribute('data-name');
            const propertyPath = dataName || id;

            const newValue = type === 'checkbox' ? e.target.checked : value;

            setFormData((prev) => {
                const updatedFormData = setNestedProperty(prev, propertyPath, newValue);
                return updatedFormData;
            });
        },
        [setNestedProperty]
    );

    // Main save settings mutation
    const saveSettingsMutation = useTrpcMutation(
        trpc.user.saveTradingProfile.mutationOptions({
            onSuccess: () => {
                // Invalidate queries to refresh data
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchTradingProfile.queryOptions({ id: tradingProfileId }).queryKey
                });
            }
        }),
        {
            successMessage: 'Trading profile saved successfully',
            fallbackErrorMessage: 'Failed to save trading profile'
        }
    );

    // Copy trading profile mutation
    const copyTradingProfileMutation = useTrpcMutation(
        trpc.user.copyTradingProfile.mutationOptions({
            onSuccess: (result) => {
                setIsCopying(false);
                setShowCopyModal(false);
                setCopyName('');

                // Navigate to the new profile
                if (result.newProfileId) {
                    void navigate(`/user/portfolioTradingProfile/${result.newProfileId}`);
                }
            },
            onError: () => {
                setIsCopying(false);
            }
        }),
        {
            successMessage: 'Trading profile copied successfully',
            fallbackErrorMessage: 'Failed to copy trading profile'
        }
    );

    // Handle copy button click
    const handleCopyClick = useCallback(() => {
        setCopyName(`${formData.name} (Copy)`);
        setShowCopyModal(true);
    }, [formData.name]);

    // Execute the copy
    const handleCopyConfirm = useCallback(() => {
        if (!tradingProfileId) return;
        setIsCopying(true);

        void copyTradingProfileMutation.mutateAsync({
            sourceProfileId: tradingProfileId,
            newName: copyName || undefined
        });
    }, [tradingProfileId, copyName, copyTradingProfileMutation]);

    // Trade filter mutation
    const tradeFilterMutation = useTrpcMutation(
        trpc.user.saveTradingProfile.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchTradingProfile.queryOptions({ id: tradingProfileId }).queryKey
                });
            }
        }),
        {
            successMessage: 'Trade filter added and trading profile saved successfully',
            fallbackErrorMessage: 'Failed to save trade filter'
        }
    );

    // Create more mutations for other actions

    const backtestSortingRuleMutation = useTrpcMutation(
        trpc.user.saveTradingProfile.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchTradingProfile.queryOptions({ id: tradingProfileId }).queryKey
                });
            }
        }),
        {
            successMessage: 'Backtest sorting rule added and trading profile saved successfully',
            fallbackErrorMessage: 'Failed to save backtest sorting rule'
        }
    );

    const priceAdjustmentRuleMutation = useTrpcMutation(
        trpc.user.saveTradingProfile.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchTradingProfile.queryOptions({ id: tradingProfileId }).queryKey
                });
            }
        }),
        {
            successMessage: 'Price adjustment rule added and trading profile saved successfully',
            fallbackErrorMessage: 'Failed to save price adjustment rule'
        }
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleSave().catch(() => undefined);
    };

    const handleSave = async () => {
        if (!canEditTradingProfile) {
            showToast('This trading profile is shared read-only.', 'error');
            return;
        }

        if (!formData.name) {
            showToast('Name is required', 'error');
            return;
        }

        showSpinner();

        try {
            const result = await saveSettingsMutation.mutateAsync({
                tradingProfileId,
                settings: formData
            });

            if (!tradingProfileId && result.settingsId) {
                void navigate(`/user/portfolioTradingProfile/${result.settingsId}`);
            }
        } finally {
            hideSpinner();
        }
    };

    // Render loading state
    if (loading && !error) {
        return (
            <Layout title="Trading Profile - Project 314">
                <div className="flex justify-center items-center min-h-[40vh]">
                    <DataLoadingSpinner text="Loading settings..." />
                </div>
            </Layout>
        );
    }

    // Render error state
    if (error) {
        return (
            <Layout title="Trading Profile - Project 314">
                <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md">{error}</div>
            </Layout>
        );
    }

    return (
        <Layout title={tradingProfileId ? `${formData.name} - Trading Profile - Project 314` : 'Create Trading Profile - Project 314'}>
            <div className="container mx-auto my-3 max-w-4xl">
                <div className="flex justify-between">
                    <div>
                        <h3 className="text-white mb-3 text-xl font-semibold">
                            {tradingProfileId ? formData.name : 'Create New Trading Profile'}
                        </h3>
                    </div>
                </div>

                {!canEditTradingProfile && (
                    <div className="mb-4 rounded-md border border-zinc-700 bg-zinc-900/60 px-4 py-3 text-sm text-zinc-300">
                        This trading profile is shared read-only. You can inspect it and use it in portfolios, but only the owner can edit
                        the live profile.
                    </div>
                )}

                <fieldset className="contents" disabled={!canEditTradingProfile}>
                    {/* Basic Settings */}
                    <BasicSettingsCard
                        formData={formData}
                        handleInputChange={handleInputChange}
                        selectedEntityTypes={selectedEntityTypes}
                        setFormData={setFormData}
                        setSelectedEntityTypes={setSelectedEntityTypes}
                    />

                    {/* Universe Screener Section */}
                    <UniverseScreenerSection
                        isUniverseScreenerActive={isUniverseScreenerActive}
                        setIsUniverseScreenerActive={setIsUniverseScreenerActive}
                        setUniverseScreenerRuleId={setUniverseScreenerRuleId}
                        tradingProfileId={tradingProfileId}
                        universeScreenerLink={tradingProfileQuery.data?.universeScreenerLink}
                        universeScreenerRuleId={universeScreenerRuleId}
                    />

                    <AeroExitSelectionSection canEdit={canManageSharing} tradingProfileId={tradingProfileId} />

                    {/* Trade Filters Section */}
                    <TradeFiltersSection
                        formData={formData}
                        saveTradeFilter={tradeFilterMutation.mutate}
                        setEditingTradeFilterId={setEditingTradeFilterId}
                        setFormData={setFormData}
                        setNewTradeFilter={setNewTradeFilter}
                        setShowTradeFilterModal={setShowTradeFilterModal}
                        setTradeFilters={setTradeFilters}
                        tradeFilters={tradeFilters}
                        tradingProfileId={tradingProfileId}
                    />

                    {/* Entry Sorting Rules Section */}
                    <EntrySortingRulesSection
                        formData={formData}
                        saveSettings={saveSettingsMutation.mutate}
                        setFormData={setFormData}
                        tradingProfileId={tradingProfileId}
                    />

                    {/* Backtest Sorting Rules Section */}
                    <BacktestSortingRulesSection
                        backtestSortingRules={backtestSortingRules}
                        formData={formData}
                        saveBacktestSortingRule={backtestSortingRuleMutation.mutate}
                        setBacktestSortingRules={setBacktestSortingRules}
                        setEditingBacktestSortingRuleId={setEditingBacktestSortingRuleId}
                        setFormData={setFormData}
                        setNewBacktestSortingRule={setNewBacktestSortingRule}
                        setShowBacktestSortingRulesModal={setShowBacktestSortingRulesModal}
                        tradingProfileId={tradingProfileId}
                    />

                    {/* Brokerage Order Rules */}
                    <BrokerageOrderRulesCard formData={formData} setFormData={setFormData} />

                    {/* Entry Collection Window Configuration */}
                    <EntryCollectionWindowCard formData={formData} setFormData={setFormData} />

                    {/* Backtester Balance Replenishment Rules */}

                    {/* Limit Price Adjustment Rules */}
                    <LimitPriceAdjustmentRulesSection
                        formData={formData}
                        priceAdjustmentRules={priceAdjustmentRules}
                        savePriceAdjustmentRule={priceAdjustmentRuleMutation.mutate}
                        setEditingLimitPriceAdjustmentRuleId={setEditingLimitPriceAdjustmentRuleId}
                        setFormData={setFormData}
                        setNewLimitPriceAdjustmentRule={setNewLimitPriceAdjustmentRule}
                        setPriceAdjustmentRules={setPriceAdjustmentRules}
                        setShowLimitPriceAdjustmentRulesModal={setShowLimitPriceAdjustmentRulesModal}
                        tradingProfileId={tradingProfileId}
                    />
                </fieldset>

                {/* Save and Duplicate Buttons */}
                <div className="flex justify-end gap-3 mb-8">
                    {tradingProfileId > 0 && (
                        <>
                            {canCopyTradingProfile && (
                                <Button disabled={isCopying} variant="outline-dark" onClick={handleCopyClick}>
                                    {isCopying ? 'Duplicating...' : 'Duplicate Profile'}
                                </Button>
                            )}
                            {canManageSharing && (
                                <Button variant="outline-dark" onClick={() => setShowShareModal(true)}>
                                    Share
                                </Button>
                            )}
                        </>
                    )}
                    {canEditTradingProfile && (
                        <Button
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md"
                            disabled={saveSettingsMutation.isPending}
                            onClick={handleSubmit}
                        >
                            {saveSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
                        </Button>
                    )}
                </div>
            </div>

            {/* Trade Filter Modal */}
            <TradeFilterModal
                editingTradeFilterId={editingTradeFilterId}
                formData={formData}
                isOpen={showTradeFilterModal}
                newTradeFilter={newTradeFilter}
                saveTradeFilter={tradeFilterMutation.mutate}
                setEditingTradeFilterId={setEditingTradeFilterId}
                setFormData={setFormData}
                setNewTradeFilter={setNewTradeFilter}
                setShowTradeFilterModal={setShowTradeFilterModal}
                setTradeFilters={setTradeFilters}
                tradeFilters={tradeFilters}
                tradingProfileId={tradingProfileId}
            />

            {/* Backtest Sorting Rules Modal */}
            <BacktestSortingRuleModal
                backtestSortingRules={backtestSortingRules}
                editingBacktestSortingRuleId={editingBacktestSortingRuleId}
                formData={formData}
                isOpen={showBacktestSortingRulesModal}
                newBacktestSortingRule={newBacktestSortingRule}
                saveBacktestSortingRule={backtestSortingRuleMutation.mutate}
                setBacktestSortingRules={setBacktestSortingRules}
                setEditingBacktestSortingRuleId={setEditingBacktestSortingRuleId}
                setFormData={setFormData}
                setNewBacktestSortingRule={setNewBacktestSortingRule}
                setShowBacktestSortingRulesModal={setShowBacktestSortingRulesModal}
                tradingProfileId={tradingProfileId}
            />

            {/* Limit Price Adjustment Rules Modal */}
            <LimitPriceAdjustmentRuleModal
                editingLimitPriceAdjustmentRuleId={editingLimitPriceAdjustmentRuleId}
                formData={formData}
                isOpen={showLimitPriceAdjustmentRulesModal}
                newLimitPriceAdjustmentRule={newLimitPriceAdjustmentRule}
                priceAdjustmentRules={priceAdjustmentRules}
                savePriceAdjustmentRule={priceAdjustmentRuleMutation.mutate}
                setEditingLimitPriceAdjustmentRuleId={setEditingLimitPriceAdjustmentRuleId}
                setFormData={setFormData}
                setNewLimitPriceAdjustmentRule={setNewLimitPriceAdjustmentRule}
                setPriceAdjustmentRules={setPriceAdjustmentRules}
                setShowLimitPriceAdjustmentRulesModal={setShowLimitPriceAdjustmentRulesModal}
                tradingProfileId={tradingProfileId}
            />

            {/* Backtester Balance Replenishment Rules Modal */}

            {/* Copy Trading Profile Modal */}
            <CopyProfileModal
                copyName={copyName}
                isCopying={isCopying}
                isOpen={showCopyModal}
                profileName={formData.name}
                setCopyName={setCopyName}
                setShowCopyModal={setShowCopyModal}
                onConfirm={handleCopyConfirm}
            />

            {tradingProfileId > 0 && (
                <ShareResourceModal
                    isOpen={showShareModal}
                    resourceLabel={formData.name || `Trading Profile ${tradingProfileId}`}
                    resourcePk={tradingProfileId}
                    resourceType="tradingProfile"
                    onClose={() => setShowShareModal(false)}
                />
            )}
        </Layout>
    );
};

export default TradingProfileEditor;
